import { useState, useEffect } from "react";
import { useUser } from "../../../hooks/useUser";
import { useQuery, useAction } from "wasp/client/operations";
import { getUserProfile, updateUserProfile } from "wasp/client/operations";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { SaveBar } from "./SaveBar";
import { EnhancedDietarySection } from "./sections/EnhancedDietarySection";
import { GoalsMacrosSection } from "./sections/GoalsMacrosSection";
import { HouseholdEquipmentSection } from "./sections/HouseholdEquipmentSection";
import { PreferencesPantrySection } from "./sections/PreferencesPantrySection";
import { NotificationsEmailSection } from "./sections/NotificationsEmailSection";
import { RecipeDNAPanel } from "./sections/RecipeDNAPanel";
import { ProgressSidebar } from "./ProgressSidebar";
import { MobileProgressFAB } from "../mobile/MobileProgressFAB";
import { EnhancedBottomSheet } from "../mobile/EnhancedBottomSheet";
import { EmailVerificationBanner } from "../EmailVerificationBanner";
import { EmailVerificationSuccessBanner } from "../Email/EmailVerificationSuccessBanner";
import { useEmailVerificationState } from "../../hooks/useEmailVerificationState";
import { ChefLexMessage } from "../Errors/ChefLexMessage";
import { ProfileToast } from "./ProfileToast";
import { User, Zap, RotateCcw, Mail, Save, X, Lock } from "lucide-react";
import {
  transformBackendToUI,
  transformUIToBackend,
} from "./KitchenProfilePage";
import type { V32UITier } from '../../../types/subscription';
// Define types and helper functions locally since we removed tier restrictions
type SubscriptionTier = V32UITier;

const getTierDisplayName = (tier: SubscriptionTier) => {
  switch (tier) {
    case "starter":
      return "Duo";
    case "core_plus":
      return "Core Plus";
    default:
      return "Demo";
  }
};

const getTierColor = (tier: SubscriptionTier) => {
  switch (tier) {
    case "starter":
      return "bg-supporting text-white";
    case "core_plus":
      return "bg-coral text-white";
    default:
      return "bg-supporting text-white";
  }
};

interface UserProfile {
  // Dietary & Restrictions
  diet: string;
  allergies: string[];
  avoids: string[];
  likes: string[];
  dislikes: string[];
  timePreference: number;
  budgetLevel: number;

  // Goals & Macros (Home Chef+)
  goal: string;
  macros: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
  proteinPriority: boolean;

  // Household & Equipment (Master Chef+)
  householdSize: number;
  equipment: string[];
  batchPrep: string;
  familyScaling: number;

  // Preferences & Pantry
  pantry: string[];
  favorites: string[];
  neverAgain: string[];
  heatLevel: number;
  reheating: string[];

  // Notifications & Email
  emailVerified: boolean;
  weeklyEmail: boolean;
  emailDay: string;
  emailTime: string;
  pdfFormat: string;
  budgetTips: boolean;

  // Units & Measurements
  unitSystem?: "auto" | "metric" | "imperial";
  temperatureUnit?: "celsius" | "fahrenheit";
  defaultVolumeUnit?: string;
  defaultWeightUnit?: string;
}

interface EnhancedKitchenProfilePageProps {
  userName: string;
  userEmail: string;
  currentTier: SubscriptionTier;
  isEmailVerified: boolean;
  authProvider: string;
  onClose: () => void;
  onUpgrade?: () => void;
}

export function EnhancedKitchenProfilePage({
  userName,
  userEmail,
  currentTier,
  isEmailVerified,
  authProvider,
  onClose,
  onUpgrade,
}: EnhancedKitchenProfilePageProps) {
  // Load user profile data from backend
  const { user, isAuthenticated } = useUser();
  const {
    data: backendProfile,
    isLoading: profileLoading,
    error: profileError,
  } = useQuery(getUserProfile, {}, { enabled: isAuthenticated });
  const updateUserProfileAction = useAction(updateUserProfile);

  const [profile, setProfile] = useState<UserProfile>(
    transformBackendToUI(null),
  );
  const [originalProfile, setOriginalProfile] = useState<UserProfile>(
    transformBackendToUI(null),
  );
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("dietary");
  const [autoSaveTimer, setAutoSaveTimer] = useState<NodeJS.Timeout | null>(
    null,
  );
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
    isVisible: boolean;
  }>({
    message: "",
    type: "info",
    isVisible: false,
  });

  // Mobile progress state
  const [showMobileProgress, setShowMobileProgress] = useState(false);

  // Email verification state
  const emailVerificationState = useEmailVerificationState(
    isEmailVerified,
    authProvider,
  );

  // Removed duplicate getTierColor function - using the one defined at the top

  // Initialize profile data when backend data loads
  useEffect(() => {
    if (backendProfile && !isInitialized) {
      const transformedProfile = transformBackendToUI(backendProfile);
      setProfile(transformedProfile);
      setOriginalProfile(transformedProfile);
      setIsInitialized(true);
    }
  }, [backendProfile, isInitialized]);

  // Track changes and handle auto-save
  useEffect(() => {
    const hasChanges =
      JSON.stringify(profile) !== JSON.stringify(originalProfile);
    setIsDirty(hasChanges);

    // Auto-save after 3 seconds of inactivity (if not in demo mode)
    if (hasChanges && currentTier !== "demo") {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }

      const timer = setTimeout(() => {
        handleAutoSave();
      }, 3000);

      setAutoSaveTimer(timer);
    }

    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
  }, [profile, originalProfile, currentTier]);

  // Calculate overall progress percentage
  const calculateProgress = (): number => {
    const progressItems = [
      {
        completed: Boolean(profile.diet && profile.diet !== "No specific diet"),
        weight: 2,
      }, // Required, weighted more
      {
        completed: Boolean((profile.allergies ?? []).length || (profile.avoids ?? []).length),
        weight: 1,
      },
      { completed: Boolean((profile.likes ?? []).length), weight: 1 },
      { completed: Boolean(profile.goal), weight: 1 },
      {
        completed: Boolean(
          (profile.householdSize ?? 1) > 1 || (profile.equipment ?? []).length,
        ),
        weight: 1,
      },
      {
        completed: Boolean((profile.favorites ?? []).length || (profile.heatLevel ?? 2) !== 2),
        weight: 1,
      },
      { completed: Boolean(profile.weeklyEmail !== undefined), weight: 1 },
    ];

    const completedWeight = progressItems.reduce(
      (acc, item) => acc + (item.completed ? item.weight : 0),
      0,
    );
    const totalWeight = progressItems.reduce(
      (acc, item) => acc + item.weight,
      0,
    );

    return totalWeight > 0
      ? Math.round((completedWeight / totalWeight) * 100)
      : 0;
  };

  const overallProgress = calculateProgress();

  const handleAutoSave = async () => {
    if (!isDirty || isSaving || currentTier === "demo") return;

    try {
      setIsSaving(true);
      const backendData = transformUIToBackend(profile);
      await updateUserProfileAction(backendData);

      setOriginalProfile(profile);
      setIsDirty(false);

      setToast({
        message: "Auto-saved ✓",
        type: "success",
        isVisible: true,
      });

      // Auto-hide toast after 2 seconds
      setTimeout(() => {
        setToast((prev) => ({ ...prev, isVisible: false }));
      }, 2000);
    } catch (error) {
      console.error("Auto-save failed:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      if (currentTier === "demo") {
        setToast({
          message: "Feature preview in demo mode",
          type: "info",
          isVisible: true,
        });
        return;
      }

      // Transform UI data to backend format and save
      const backendData = transformUIToBackend(profile);
      await updateUserProfileAction(backendData);

      setOriginalProfile(profile);
      setIsDirty(false);

      setToast({
        message: "✅ Preferences saved successfully!",
        type: "success",
        isVisible: true,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save profile";
      setSaveError(errorMessage);
      setToast({
        message: errorMessage,
        type: "error",
        isVisible: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setProfile(originalProfile);
    setIsDirty(false);
    setSaveError(null);

    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
      setAutoSaveTimer(null);
    }
  };

  const updateProfile = (updates: Partial<UserProfile>) => {
    setProfile((prev) => ({ ...prev, ...updates }));
  };

  const isTabLocked = (tab: string) => {
    // Remove all tier locks - all features available to all users
    return false;
  };

  const getTabRequirement = (tab: string): string => {
    switch (tab) {
      case "household":
        return "Advanced";
      case "goals":
        return "Optional";
      default:
        return "All Plans";
    }
  };

  // Show loading state while profile data is being fetched
  if (profileLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="mx-auto mb-6 h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-warning"></div>
          <h2 className="mb-2 text-lg font-semibold text-gray-900">Loading your kitchen profile</h2>
          <p className="text-sm text-gray-600">We're gathering your preferences and settings...</p>
          
          {/* Progress indicators */}
          <div className="mt-6 flex justify-center space-x-2">
            <div className="h-2 w-2 bg-warning rounded-full animate-pulse"></div>
            <div className="h-2 w-2 bg-warning rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="h-2 w-2 bg-warning rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if profile data failed to load
  if (profileError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="max-w-md text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-red-100 p-3">
              <X className="h-8 w-8 text-red-600" />
            </div>
          </div>
          <h2 className="mb-2 text-xl font-semibold text-gray-900">
            Unable to load your profile
          </h2>
          <p className="mb-6 text-sm text-gray-600">
            We're having trouble loading your kitchen profile. This might be a temporary issue.
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              onClick={() => window.location.reload()}
              className="bg-warning hover:bg-warning/90 text-ink"
            >
              Try Again
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
            >
              Go Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12 ring-2 ring-gray-100">
                <AvatarFallback className="bg-gradient-to-br from-warning to-warning/80 text-white font-semibold">
                  {userName.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="mb-1 flex items-center gap-3">
                  <h1 className="font-header text-xl font-semibold text-gray-900">
                    Kitchen Profile
                  </h1>
                  <Badge className={`text-xs font-medium px-2 py-1 ${getTierColor(currentTier)}`}>
                    {getTierDisplayName(currentTier)}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Tell us how you like to cook—we'll handle the planning.
                </p>
                <p className="mt-1 text-xs text-gray-500">{userEmail}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isDirty && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="border-coral text-coral desktop-hover:bg-coral/10"
                >
                  <Save className="mr-2 h-4 w-4" />
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              )}
              <Button variant="ghost" onClick={onClose} size="sm">
                <X className="mr-2 h-4 w-4" />
                Close
              </Button>
            </div>
          </div>
        </div>

        {/* Save Bar */}
        {isDirty && (
          <SaveBar
            onSave={handleSave}
            onDiscard={handleDiscard}
            isSaving={isSaving}
          />
        )}
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          {/* Main Profile Form */}
          <div className="lg:col-span-3 space-y-6">
            {saveError && (
              <div className="mb-6">
                <ChefLexMessage
                  type="GENERATION_FAILED"
                  onPrimaryAction={() => setSaveError(null)}
                />
              </div>
            )}

            {/* Email Verification Banners */}
            {emailVerificationState.showUnverifiedBanner && (
              <EmailVerificationBanner
                onDismiss={emailVerificationState.dismissUnverifiedBanner}
              />
            )}

            <EmailVerificationSuccessBanner isEmailVerified={isEmailVerified} />

            <Card className="border border-gray-100 bg-white shadow-sm overflow-hidden">
              <CardContent className="p-0">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <div className="border-b bg-gradient-to-r from-gray-50 to-gray-50/80 px-6 pt-6">
                    <TabsList className="h-auto w-full bg-transparent p-0">
                      <div className="grid w-full grid-cols-2 gap-3 lg:grid-cols-6 pb-2">
                        <TabsTrigger
                          value="dietary"
                          className="whitespace-normal rounded-lg px-3 py-3 text-center text-xs leading-tight transition-all duration-200 data-[state=active]:border-2 data-[state=active]:border-warning data-[state=active]:bg-white data-[state=active]:text-warning desktop-hover:bg-white/50 sm:text-sm"
                        >
                          <div className="space-y-1">
                            <div>Dietary & Restrictions</div>
                            <div className="text-xs text-gray-500">
                              Required
                            </div>
                          </div>
                        </TabsTrigger>
                        <TabsTrigger
                          value="goals"
                          className={`relative whitespace-normal rounded-lg px-3 py-3 text-center text-xs leading-tight transition-all duration-200 data-[state=active]:border-2 data-[state=active]:border-warning data-[state=active]:bg-white data-[state=active]:text-warning desktop-hover:bg-white/50 sm:text-sm ${
                            isTabLocked("goals") ? "opacity-60" : ""
                          }`}
                          disabled={isTabLocked("goals")}
                        >
                          <div className="space-y-1">
                            <div className="flex items-center gap-1">
                              Goals & Macros
                              {isTabLocked("goals") && (
                                <Lock className="h-3 w-3 text-gray-400" />
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              {isTabLocked("goals") ? getTabRequirement("goals") : "All Plans"}
                            </div>
                          </div>
                        </TabsTrigger>
                        <TabsTrigger
                          value="household"
                          className={`relative whitespace-normal rounded-lg px-3 py-3 text-center text-xs leading-tight transition-all duration-200 data-[state=active]:border-2 data-[state=active]:border-warning data-[state=active]:bg-white data-[state=active]:text-warning desktop-hover:bg-white/50 sm:text-sm ${
                            isTabLocked("household") ? "opacity-60" : ""
                          }`}
                          disabled={isTabLocked("household")}
                        >
                          <div className="space-y-1">
                            <div className="flex items-center gap-1">
                              Household & Equipment
                              {isTabLocked("household") && (
                                <Lock className="h-3 w-3 text-gray-400" />
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              {isTabLocked("household") ? getTabRequirement("household") : "Advanced"}
                            </div>
                          </div>
                        </TabsTrigger>
                        <TabsTrigger
                          value="preferences"
                          className="whitespace-normal rounded-lg px-3 py-3 text-center text-xs leading-tight transition-all duration-200 data-[state=active]:border-2 data-[state=active]:border-warning data-[state=active]:bg-white data-[state=active]:text-warning desktop-hover:bg-white/50 sm:text-sm"
                        >
                          <div className="space-y-1">
                            <div>Preferences & Pantry</div>
                            <div className="text-xs text-gray-500">
                              Optional
                            </div>
                          </div>
                        </TabsTrigger>
                        <TabsTrigger
                          value="units"
                          className="whitespace-normal rounded-lg px-3 py-3 text-center text-xs leading-tight transition-all duration-200 data-[state=active]:border-2 data-[state=active]:border-warning data-[state=active]:bg-white data-[state=active]:text-warning desktop-hover:bg-white/50 sm:text-sm"
                        >
                          <div className="space-y-1">
                            <div>Units & Measurements</div>
                            <div className="text-xs text-gray-500">
                              Settings
                            </div>
                          </div>
                        </TabsTrigger>
                        <TabsTrigger
                           value="notifications"
                           className="whitespace-normal rounded-lg px-3 py-3 text-center text-xs leading-tight transition-all duration-200 data-[state=active]:border-2 data-[state=active]:border-warning data-[state=active]:bg-white data-[state=active]:text-warning desktop-hover:bg-white/50 sm:text-sm"
                         >
                           <div className="space-y-1">
                             <div>Notifications & Email</div>
                             <div className="text-xs text-gray-500">
                               Settings
                             </div>
                           </div>
                         </TabsTrigger>
                         <TabsTrigger
                           value="dna"
                           className="whitespace-normal rounded-lg px-3 py-3 text-center text-xs leading-tight transition-all duration-200 data-[state=active]:border-2 data-[state=active]:border-warning data-[state=active]:bg-white data-[state=active]:text-warning desktop-hover:bg-white/50 sm:text-sm"
                         >
                           <div className="space-y-1">
                             <div>Recipe DNA</div>
                             <div className="text-xs text-gray-500">
                               Taste Profile
                             </div>
                           </div>
                         </TabsTrigger>
                      </div>
                    </TabsList>
                  </div>

                  <div className="p-6">
                    {/* Upgrade prompt for locked tabs */}
                    {(activeTab === "household" && isTabLocked("household")) && (
                      <div className="mb-6 rounded-lg border border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50 p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            <Lock className="h-5 w-5 text-orange-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-sm font-medium text-orange-900">
                              Master Chef Feature
                            </h3>
                            <p className="mt-1 text-sm text-orange-800">
                              Household size settings and advanced equipment preferences are available with Master Chef plan.
                            </p>
                            {onUpgrade && (
                              <Button
                                size="sm"
                                onClick={onUpgrade}
                                className="mt-3 bg-orange-600 hover:bg-orange-700 text-white"
                              >
                                Upgrade to Master Chef
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    <TabsContent value="dietary" className="mt-0">
                      <EnhancedDietarySection
                        profile={profile}
                        onUpdate={updateProfile}
                        currentTier={currentTier}
                      />
                    </TabsContent>

                    <TabsContent value="goals" className="mt-0">
                      <GoalsMacrosSection
                        profile={profile}
                        onUpdate={updateProfile}
                        currentTier={currentTier}
                        isLocked={isTabLocked("goals")}
                      />
                    </TabsContent>

                    <TabsContent value="household" className="mt-0">
                      <HouseholdEquipmentSection
                        profile={profile}
                        onUpdate={updateProfile}
                        currentTier={currentTier}
                        isLocked={isTabLocked("household")}
                      />
                    </TabsContent>

                    <TabsContent value="preferences" className="mt-0">
                      <PreferencesPantrySection
                        profile={profile}
                        onUpdate={updateProfile}
                        currentTier={currentTier}
                      />
                    </TabsContent>

                    <TabsContent value="units" className="mt-0">
                      <div className="space-y-6">
                        <div>
                          <h3 className="mb-4 text-lg font-medium text-gray-900">
                            Units & Measurements
                          </h3>
                          <p className="mb-6 text-sm text-gray-600">
                            Customize how measurements are displayed in your
                            recipes and shopping lists.
                          </p>
                        </div>

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          <div className="space-y-4">
                            <div>
                              <label className="mb-2 block text-sm font-medium text-gray-700">
                                Unit System
                              </label>
                              <select
                                value={profile.unitSystem || "auto"}
                                onChange={(e) =>
                                  updateProfile({
                                    unitSystem: e.target.value as
                                      | "auto"
                                      | "metric"
                                      | "imperial",
                                  })
                                }
                                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-warning"
                              >
                                <option value="auto">
                                  Auto (Smart Detection)
                                </option>
                                <option value="metric">Metric</option>
                                <option value="imperial">Imperial</option>
                              </select>
                              <p className="mt-1 text-xs text-gray-500">
                                Choose your preferred measurement system or let
                                us detect it automatically.
                              </p>
                            </div>

                            <div>
                              <label className="mb-2 block text-sm font-medium text-gray-700">
                                Temperature Unit
                              </label>
                              <select
                                value={profile.temperatureUnit || "celsius"}
                                onChange={(e) =>
                                  updateProfile({
                                    temperatureUnit: e.target.value as
                                      | "celsius"
                                      | "fahrenheit",
                                  })
                                }
                                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-warning"
                              >
                                <option value="celsius">Celsius (°C)</option>
                                <option value="fahrenheit">
                                  Fahrenheit (°F)
                                </option>
                              </select>
                              <p className="mt-1 text-xs text-gray-500">
                                How temperatures should be displayed in recipes.
                              </p>
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <label className="mb-2 block text-sm font-medium text-gray-700">
                                Default Volume Unit
                              </label>
                              <select
                                value={profile.defaultVolumeUnit || "cup"}
                                onChange={(e) =>
                                  updateProfile({
                                    defaultVolumeUnit: e.target.value,
                                  })
                                }
                                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-warning"
                              >
                                <option value="ml">Milliliters (ml)</option>
                                <option value="cup">Cups</option>
                                <option value="tbsp">Tablespoons (tbsp)</option>
                                <option value="tsp">Teaspoons (tsp)</option>
                                <option value="liter">Liters</option>
                              </select>
                              <p className="mt-1 text-xs text-gray-500">
                                Your preferred unit for liquid measurements.
                              </p>
                            </div>

                            <div>
                              <label className="mb-2 block text-sm font-medium text-gray-700">
                                Default Weight Unit
                              </label>
                              <select
                                value={profile.defaultWeightUnit || "g"}
                                onChange={(e) =>
                                  updateProfile({
                                    defaultWeightUnit: e.target.value,
                                  })
                                }
                                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-warning"
                              >
                                <option value="g">Grams (g)</option>
                                <option value="kg">Kilograms (kg)</option>
                                <option value="oz">Ounces (oz)</option>
                                <option value="lb">Pounds (lb)</option>
                              </select>
                              <p className="mt-1 text-xs text-gray-500">
                                Your preferred unit for weight measurements.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-500">
                                <span className="text-xs font-bold text-white">
                                  i
                                </span>
                              </div>
                            </div>
                            <div>
                              <h4 className="mb-1 text-sm font-medium text-blue-900">
                                Smart Unit Conversion
                              </h4>
                              <p className="text-sm text-blue-800">
                                Our system automatically converts between
                                compatible units (e.g., cups to ml, pounds to
                                grams) while preserving your preferred display
                                units. Mixed units in recipes are intelligently
                                combined for cleaner shopping lists.
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </TabsContent>

                     <TabsContent value="notifications" className="mt-0">
                       <NotificationsEmailSection
                         profile={profile}
                         onUpdate={updateProfile}
                         currentTier={currentTier}
                       />
                     </TabsContent>

                     <TabsContent value="dna" className="mt-0">
                       <RecipeDNAPanel
                         profile={{
                           spiceLevel: profile.heatLevel ?? 2,
                           sweetnessLevel: 2,
                           cookingTime: profile.timePreference || 30,
                           complexityLevel: "intermediate",
                           dietaryRestrictions: profile.allergies || [],
                           customRestrictions: profile.avoids || [],
                           likedCuisines: profile.likes || [],
                           dislikedCuisines: profile.dislikes || [],
                           cookingSkill: "intermediate"
                         }}
                         onUpdate={(updates) => {
                           // Map RecipeDNA updates back to profile format
                           const mappedUpdates: Partial<UserProfile> = {};

                           if (updates.spiceLevel !== undefined) {
                             mappedUpdates.heatLevel = updates.spiceLevel;
                           }
                           if (updates.sweetnessLevel !== undefined) {
                             // Store sweetness level in a new field or extend existing profile
                           }
                           if (updates.cookingTime !== undefined) {
                             mappedUpdates.timePreference = updates.cookingTime;
                           }
                           if (updates.complexityLevel !== undefined) {
                             // Store complexity level
                           }
                           if (updates.dietaryRestrictions !== undefined) {
                             mappedUpdates.allergies = updates.dietaryRestrictions;
                           }
                           if (updates.customRestrictions !== undefined) {
                             mappedUpdates.avoids = updates.customRestrictions;
                           }
                           if (updates.likedCuisines !== undefined) {
                             mappedUpdates.likes = updates.likedCuisines;
                           }
                           if (updates.dislikedCuisines !== undefined) {
                             mappedUpdates.dislikes = updates.dislikedCuisines;
                           }
                           if (updates.cookingSkill !== undefined) {
                             // Store cooking skill
                           }

                           updateProfile(mappedUpdates);
                         }}
                         currentTier={currentTier}
                       />
                     </TabsContent>
                   </div>
                 </Tabs>
               </CardContent>
             </Card>
          </div>

          {/* Progress Sidebar */}
          <aside className="hidden space-y-6 lg:block" aria-label="Profile progress and quick actions">
            <div className="sticky top-24 space-y-6">
              <ProgressSidebar
                currentTab={activeTab}
                profileData={profile}
                onTabChange={setActiveTab}
              />

              {/* Quick Actions */}
              <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-sm">
                  <Zap className="h-4 w-4 text-warning" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  onClick={() => {
                    if (isDirty) {
                      setToast({
                        message:
                          "Save changes first to regenerate with new profile",
                        type: "info",
                        isVisible: true,
                      });
                      return;
                    }
                    if (onUpgrade) {
                      onUpgrade();
                      setToast({
                        message:
                          "Cooking up a fresh plan with your new profile!",
                        type: "success",
                        isVisible: true,
                      });
                    }
                  }}
                  className="w-full justify-start"
                  variant="outline"
                  disabled={isDirty}
                  title="Re-run plan with new profile"
                >
                  <RotateCcw className="mr-2 h-4 w-4 flex-shrink-0" />
                  <span className="max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
                    Re-run plan with new profile
                  </span>
                </Button>
                <Button
                  onClick={() => {
                    if (isDirty) {
                      setToast({
                        message: "Save changes first to preview email",
                        type: "info",
                        isVisible: true,
                      });
                      return;
                    }
                  }}
                  className="w-full justify-start"
                  variant="outline"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Preview weekly email
                </Button>
                {isDirty && (
                  <p className="text-xs text-gray-500">
                    Save changes to use quick actions
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Demo Mode Card */}
            {currentTier === "demo" && (
              <Card className="border-orange-200 bg-gradient-to-br from-orange-50 to-amber-50">
                <CardContent className="p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <User className="h-4 w-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-900">
                      Demo Mode
                    </span>
                  </div>
                  <p className="mb-3 text-xs text-orange-800 leading-relaxed">
                    Changes won't be saved in demo mode. Start your free trial
                    to unlock full profile customization.
                  </p>
                  <Button
                    size="sm"
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={onUpgrade}
                  >
                    Start Free Trial
                  </Button>
                </CardContent>
              </Card>
            )}
            </div>
          </aside>
        </div>
      </main>

      {/* Mobile Progress FAB - Enhanced for better accessibility */}
      <div className="lg:hidden">
        <MobileProgressFAB
          progress={overallProgress}
          onClick={() => setShowMobileProgress(true)}
        />
      </div>

      {/* Mobile Progress Bottom Sheet - Enhanced UX */}
      <EnhancedBottomSheet
        isOpen={showMobileProgress}
        onClose={() => setShowMobileProgress(false)}
        title={`Profile Progress (${overallProgress}% complete)`}
      >
        <div className="pb-4">
          <ProgressSidebar
            currentTab={activeTab}
            profileData={profile}
            onTabChange={(tab) => {
              setActiveTab(tab);
              setShowMobileProgress(false); // Close sheet when navigating to a tab
            }}
          />
        </div>
      </EnhancedBottomSheet>

      {/* Toast Notifications */}
      <ProfileToast
        message={toast.message}
        type={toast.type}
        isVisible={toast.isVisible}
        onClose={() => setToast((prev) => ({ ...prev, isVisible: false }))}
      />
    </div>
  );
}
